import {
	FormattedCurrency,
	IDataListColumn,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import styled from "styled-components";
import { EventCostCategoryIndexResource } from "../../../../../shared/types/event";
import { costCategoryService } from "../../../shared/api/costCategoryService";

const CostDifferenceWrapper = styled.div<{ $color: "success" | "danger" }>`
	display: grid;
	grid-template-columns: max-content max-content;
	gap: ${({ theme }) => theme.spacing(2)};
	color: ${({ theme, $color }) => theme.colors.palette[$color].main.base};
`;

export const EventCostCategoryList: FC = () => {
	const { t } = useTranslation();
	const { eventId = "" } = useParams<"eventId">();

	const dataListColumns: IDataListColumn<EventCostCategoryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Cost category"),
				valueKey: "name",
				render: ({ name }) => name,
				type: "value",
			},
			{
				heading: t("Planned cost"),
				valueKey: "plannedCost",
				type: "value",
				render: (costCategory) =>
					costCategory.plannedCost === null || !costCategory.currency ? (
						"-"
					) : (
						<FormattedCurrency value={costCategory.plannedCost} currencyType={costCategory.currency} />
					),
			},
			{
				heading: t("Actual cost"),
				valueKey: "actualCost",
				type: "value",
				render: (costCategory) =>
					costCategory.actualCost === null || !costCategory.currency ? (
						"-"
					) : (
						<FormattedCurrency value={costCategory.actualCost} currencyType={costCategory.currency} />
					),
			},
			{
				heading: t("Difference"),
				valueKey: "currency",
				type: "value",
				render: (costCategory) => {
					if (costCategory.plannedCost === null || costCategory.actualCost === null || !costCategory.currency) {
						return "-";
					}
					const dif = costCategory.plannedCost - costCategory.actualCost;
					return (
						<CostDifferenceWrapper $color={dif > 0 ? "success" : "danger"}>
							<FormattedCurrency value={dif} currencyType={costCategory.currency} />
							<span>
								{costCategory.plannedCost !== 0 &&
									`(${Math.round((costCategory.actualCost / costCategory.plannedCost) * 100)}%)`}
							</span>
						</CostDifferenceWrapper>
					);
				},
			},
		],
		[t],
	);

	return (
		<PaginatedAutoDataList
			name="eventCostCategories"
			empty={t("No cost categories available.")}
			heading={t("Cost categories")}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(
				costCategoryService.indexByEventKey(eventId),
				(config: PaginatedListFetcherConfig<EventCostCategoryIndexResource>) =>
					costCategoryService.indexByEvent(config, eventId),
			)}
			initialSearch={{
				search: "",
				searchFields: [{ name: "costCategory.name", label: t("Cost category") }],
			}}
		/>
	);
};
